import { useEffect, useRef } from 'react';

const VIDEO_SRC = '/videos/student-portal.mp4';

// Plays while at least half of the video is on screen and pauses when it
// scrolls away. On the way in, the frame grows from 90% to full size, driven by
// scroll position so it tracks the reader's scrolling exactly.
export default function PortalVideo() {
  const frameRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const frame = frameRef.current;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      frame.style.setProperty('--reveal', 1);
      return undefined;
    }

    let raf = 0;
    const update = () => {
      raf = 0;
      const rect = frame.getBoundingClientRect();
      const vh = window.innerHeight;
      // 0 when the frame's top touches the bottom of the viewport, 1 once its
      // centre is a little below the middle of the screen.
      const start = vh;
      const end = vh * 0.6 - rect.height / 2;
      const progress = Math.min(1, Math.max(0, (start - rect.top) / (start - end)));
      frame.style.setProperty('--reveal', progress.toFixed(3));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="portal-video" ref={frameRef}>
      <video
        ref={videoRef}
        className="portal-video__media"
        src={VIDEO_SRC}
        muted
        loop
        playsInline
        preload="metadata"
        aria-label="A walkthrough of the Student For Hire student job portal"
      />
    </div>
  );
}
